// Native QR / barcode scanner bridge.
//
// Wraps @capacitor-mlkit/barcode-scanning so callers don't have to handle
// permission prompts, Google Play Services availability, or web fallbacks.
//
// On the web build all exports degrade to a "not supported" result — for now.
// Phase 3 will add a getUserMedia() fallback so admins can scan tickets from
// a desktop browser too.

import { BarcodeScanner } from '@capacitor-mlkit/barcode-scanning';
import { isNative, platform } from './native';

/** Returns true if a native scanner is available on this device. */
export async function isScannerSupported() {
  if (!isNative()) return false;
  try {
    const { supported } = await BarcodeScanner.isSupported();
    return !!supported;
  } catch {
    return false;
  }
}

/** Prompt for camera permission. Returns 'granted' | 'denied' | ... */
export async function ensureScannerPermission() {
  if (!isNative()) return 'unsupported';
  try {
    let { camera } = await BarcodeScanner.checkPermissions();
    if (camera === 'prompt' || camera === 'prompt-with-rationale') {
      ({ camera } = await BarcodeScanner.requestPermissions());
    }
    return camera;
  } catch (e) {
    console.warn('[qrScanner] permission check failed:', e?.message);
    return 'denied';
  }
}

/**
 * Scan a single barcode/QR. Resolves to the decoded string or `null` if the
 * user cancelled or an error occurred. ML Kit must be installed first on
 * Android — `installGoogleBarcodeScannerModule()` is invoked lazily.
 */
export async function scanOnce({ formats = ['QR_CODE'] } = {}) {
  if (!isNative()) return null;
  const perm = await ensureScannerPermission();
  if (perm !== 'granted') return null;

  try {
    if (platform() === 'android') {
      // Lazy install of the Google Barcode Scanner module (≈2 MB) — only
      // happens once per device, no-op afterwards.
      const { available } = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
      if (!available) {
        await BarcodeScanner.installGoogleBarcodeScannerModule();
      }
    }
    const { barcodes } = await BarcodeScanner.scan({ formats });
    return barcodes?.[0]?.rawValue || null;
  } catch (e) {
    console.warn('[qrScanner] scan failed:', e?.message);
    return null;
  }
}
